/**
 * OrganizationsList Component
 *
 * Displays list of organizations with their audit metrics (admin view)
 */

import React, { useCallback } from 'react';
import { BusinessOutlined as EmptyIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { useAdminOrganizationsList } from '../../hooks/admin';

import { OrganizationRow } from './OrganizationRow';

interface OrganizationsListProps {
  /** Callback when an organization is selected */
  onOrganizationSelect?: (organizationId: string, organizationName: string | null) => void;
}

export const OrganizationsList: React.FC<OrganizationsListProps> = ({ onOrganizationSelect }) => {
  const theme = useTheme();
  const { organizations, total, isLoading, error, refetch, isEmpty } = useAdminOrganizationsList();

  const handleOrgClick = useCallback(
    (organizationId: string) => {
      const org = organizations.find(o => o.organization_id === organizationId);
      onOrganizationSelect?.(organizationId, org?.organization_name || null);
    },
    [organizations, onOrganizationSelect],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading organizations</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Organizations
          </Typography>
          {!isLoading && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {total} {total === 1 ? 'organization' : 'organizations'} with audit data
            </Typography>
          )}
        </Box>
      </Box>

      {/* List Container */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Loading State */}
        {isLoading && organizations.length === 0 ? (
          <Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Skeleton variant="circular"  sx={{ width: 44, height: 44 }}/>
                <Box sx={{ flex: 1 }}>
                  <Skeleton sx={{width: "40%", height: 20,  mb: 0.5 }} />
                  <Skeleton  sx={{ width: "30%", height: 16 }}/>
                </Box>
                <Skeleton  sx={{ width: 60, height: 20 }}/>
                <Skeleton  sx={{ width: 60, height: 20 }}/>
                <Skeleton  sx={{ width: 80, height: 24 }}/>
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          /* Empty State */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: theme.palette.grey[100],
                mb: 2,
              }}
            >
              <EmptyIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
            </Box>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
              No organizations found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
            >
              Audit data will appear here once organizations have audited notes
            </Typography>
          </Box>
        ) : (
          /* Organization List */
          <Box sx={{ position: 'relative' }}>
            {isLoading && organizations.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {organizations.map(org => (
              <OrganizationRow
                key={org.organization_id}
                organization={org}
                onClick={handleOrgClick}
              />
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OrganizationsList;

